<template>
  <FormModal
    header="Select available time slot"
    :name="name"
    width-class="tw-max-w-[850px]"
  >
    <FormModalBody
      padding-class="tw-p-4"
      footer-button-text="Save"
      @submit="handleSubmit"
    >
      <div class="tw-space-y-4">
        <div class="tw-text-center tw-text-sm tw-text-neutral-400">
          Calendar shows dates in {{ timezone }} timezone.
        </div>
        <div class="tw-flex tw-items-start tw-justify-between">
          <CalendarPaginationButton
            @click="decrementWeek"
            :disabled="!canWeekBeDecremented || isLoadingAvailableSlots"
            class="tw-mt-3"
            direction="left"
          />

          <div class="tw-relative tw-grow">
            <transition name="fade">
              <div
                v-if="isLoadingAvailableSlots"
                class="tw-absolute tw-grid tw-h-full tw-w-full tw-place-items-center tw-rounded-lg tw-bg-black/10"
              >
                <LoadingCircle size="32" />
              </div>
            </transition>
            <div
              class="tw-grid tw-grid-cols-2 tw-divide-x tw-divide-neutral-200 tw-text-sm sm:tw-grid-cols-4 md:tw-grid-cols-7"
            >
              <div
                v-for="day in weekDays"
                :key="day.formattedDate"
                class="tw-px-2 tw-pb-12 tw-pt-2 tw-text-center"
              >
                <div class="tw-font-semibold">
                  {{ formatDate(day.date, 'cccc') }}
                </div>
                <div>
                  {{ formatDate(day.date, 'dd MMM.') }}
                </div>
                <div
                  v-if="availableSlots[day.formattedDate]"
                  class="tw-mt-4 tw-space-y-1.5"
                >
                  <CalendarAvailableSlotButton
                    v-for="slot in availableSlots[day.formattedDate]"
                    :key="slot.startsAt"
                    :starts-at="slot.startsAt"
                    :is-selected="slot.startsAt === modelValue"
                    :disabled="isLoadingAvailableSlots"
                    @click="emit('update:modelValue', slot.startsAt)"
                  />
                </div>
                <div v-else class="tw-mt-6 tw-select-none tw-text-neutral-200">
                  -
                </div>
              </div>
            </div>
          </div>

          <CalendarPaginationButton
            @click="incrementWeek"
            class="tw-mt-3"
            :disabled="!canWeekBeIncremented || isLoadingAvailableSlots"
            direction="right"
            test_id="next-week-button"
          />
        </div>
        <div
          v-if="availableSlotsRequestStatus === RequestStatus.ERROR"
          class="tw-text-center tw-text-sm tw-text-error-500"
        >
          Something went wrong. Please try again.
        </div>
      </div>
    </FormModalBody>
  </FormModal>
</template>

<script setup>
import LoadingCircle from '@/shared/components/LoadingCircle.vue'
import { RequestStatus } from '@/shared/utils/requestStatus.js'
import CalendarPaginationButton from '@/shared/components/calendar/CalendarPaginationButton.vue'
import CalendarAvailableSlotButton from '@/shared/components/calendar/CalendarAvailableSlotButton.vue'
import FormModal from '@/shared/components/modals/FormModal/FormModal.vue'
import FormModalBody from '@/shared/components/modals/FormModal/FormModalBody.vue'
import { DateTime } from 'luxon'
import camelcaseKeys from 'camelcase-keys'
import axios from 'axios'
import { useScreenWidth } from '@/shared/composables/useScreenWidth.js'
import { computed, onBeforeMount, ref, watch } from 'vue'
import { usePage } from '@inertiajs/vue3'
import { useDateManager } from '@/shared/composables/useDateManager.js'
import { useModals } from '@/shared/composables/useModals.js'
import { useCurrentUser } from '@/shared/composables/useCurrentUser.js'

// Each session can be booked 2 days before the session starts, so we have to make sure that we start fetching available slots on correct date
const SHIFT_CALENDAR_BY_DAYS = 2
const AVAILABLE_SLOTS_KEY_DATE_FORMAT = 'dd-MM-yyyy'

const emit = defineEmits(['update:modelValue', 'submit'])

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  selectedSessionDuration: {
    type: Number,
    required: true,
  },
  modelValue: {
    type: Number,
    default: 0,
  },
})

const { screenWidth, tailwindScreens } = useScreenWidth()
const page = usePage()
const { formatDate } = useDateManager()
const { closeModal } = useModals()
const { timezone } = useCurrentUser()

const firstDayInRange = ref(undefined)
const lastDayInRange = ref(undefined)
const availableSlots = ref({})
const availableSlotsCount = ref(0)
const availableSlotsRequestStatus = ref(RequestStatus.IDLE)

const canWeekBeDecremented = computed(() => {
  const subtractedDate = firstDayInRange.value.minus({
    days: SHIFT_CALENDAR_BY_DAYS,
  })
  return !DateTime.now().hasSame(subtractedDate, 'day')
})
const canWeekBeIncremented = computed(() => {
  return lastDayInRange.value.minus({ months: 2 }) < DateTime.now()
})
const weekDays = computed(() => {
  return Array.from({ length: daysToDisplay.value }, (_, index) => {
    const date = firstDayInRange.value.plus({ days: index })
    return {
      date,
      formattedDate: date.toFormat(AVAILABLE_SLOTS_KEY_DATE_FORMAT),
    }
  })
})
const isLoadingAvailableSlots = computed(() => {
  return availableSlotsRequestStatus.value === RequestStatus.LOADING
})
const daysToDisplay = computed(() => {
  if (screenWidth.value < tailwindScreens.sm) {
    return 2
  }
  if (screenWidth.value < tailwindScreens.md) {
    return 4
  }
  return 7
})

watch(
  () => props.selectedSessionDuration,
  () => {
    fetchAvailableSlots({
      from: firstDayInRange.value,
      to: lastDayInRange.value,
    })
  },
)
// Each time user changes the screen size, we want to reset the calendar and fetch slots for the new range
watch(screenWidth, () => {
  initCalendar()
})

const initDaysInRange = () => {
  firstDayInRange.value = DateTime.now()
    .startOf('day')
    .plus({ days: SHIFT_CALENDAR_BY_DAYS })
  lastDayInRange.value = DateTime.now()
    .endOf('day')
    .plus({ days: daysToDisplay.value - 1 + SHIFT_CALENDAR_BY_DAYS })
}

const fetchAvailableSlots = async ({ from, to }) => {
  availableSlotsRequestStatus.value = RequestStatus.LOADING
  try {
    // Checking whether NOW + SHIFT_CALENDAR_BY_DAYS is the same day what from
    // If so, it means that we are on the first page, and we should take into account current time (hours, minutes, seconds)
    // If not, it means that we are in the future, and we start from 00:00:00
    const twoDaysFromNow = DateTime.now().plus({
      days: SHIFT_CALENDAR_BY_DAYS,
    })
    const fromTimestamp = twoDaysFromNow.hasSame(from, 'day')
      ? twoDaysFromNow.toSeconds()
      : from.toSeconds()
    const toTimestamp = to.toSeconds()
    const { data } = await axios.get(
      `/mentors/${page.props.mentor.id}/availability`,
      {
        params: {
          from: Math.trunc(fromTimestamp),
          to: Math.trunc(toTimestamp),
          slot: props.selectedSessionDuration,
        },
      },
    )

    availableSlotsCount.value = data.length
    availableSlots.value = camelcaseKeys(data).reduce((accumulator, date) => {
      const formattedDay = formatDate(
        date.startsAt,
        AVAILABLE_SLOTS_KEY_DATE_FORMAT,
      )
      if (accumulator[formattedDay]) {
        accumulator[formattedDay] = [...accumulator[formattedDay], date]
      } else {
        accumulator[formattedDay] = [date]
      }
      return accumulator
    }, {})

    firstDayInRange.value = from
    lastDayInRange.value = to
    availableSlotsRequestStatus.value = RequestStatus.SUCCESS
  } catch (error) {
    console.log(error)
    availableSlotsRequestStatus.value = RequestStatus.ERROR
  }
}
const initCalendar = () => {
  initDaysInRange()
  fetchAvailableSlots({
    from: firstDayInRange.value,
    to: lastDayInRange.value,
  })
}

const incrementWeek = () => {
  fetchAvailableSlots({
    from: firstDayInRange.value.plus({ days: daysToDisplay.value }),
    to: lastDayInRange.value.plus({ days: daysToDisplay.value }),
  })
}

const decrementWeek = () => {
  fetchAvailableSlots({
    from: firstDayInRange.value.minus({ days: daysToDisplay.value }),
    to: lastDayInRange.value.minus({ days: daysToDisplay.value }),
  })
}

const handleSubmit = () => {
  closeModal(props.name)
  emit('submit')
}

onBeforeMount(() => {
  initCalendar()
})
</script>
